/**
 * brochureVectorPDF.js — Vector PDF generator dispatcher per i 4 layout.
 *
 * Sostituisce il flusso `html2canvas → JPEG → addImage` con generazione
 * nativa via jsPDF. Conseguenze:
 *   • testi VECTOR: crisp a qualsiasi zoom, ricerca/copia funzionante, ~1-2 MB
 *   • niente più cut/sovrapposizioni causati da html2canvas pseudo-elements
 *   • niente più dipendenza dai Google Fonts caricati (usiamo i 14 standard PDF)
 *
 * Layout supportati: elegante, luxury, moderno, minimal — vedi i file dedicati.
 */

import jsPDF from "jspdf";
import { A4_W, A4_H, hexToRgb, setFill, setStroke, setText, safe, getAttrs, cleanCity, loadImageAsJpegDataUrl, loadAgencyLogo, addPhotoCover, shortenEmail, cityIfNotInTitle } from "./brochurePdfShared";
import {
  buildCoverLuxury,
  buildDescriptionLuxury,
  buildGalleryLuxury,
  buildContactsLuxury,
} from "./brochureVectorLuxury";
import {
  buildCoverModerno,
  buildDescriptionModerno,
  buildGalleryModerno,
  buildContactsModerno,
} from "./brochureVectorModerno";
import {
  buildCoverMinimal,
  buildDescriptionMinimal,
  buildGalleryMinimal,
  buildContactsMinimal,
} from "./brochureVectorMinimal";

// ─── Page 1: COVER (Elegante) ──────────────────────────────────────
async function buildCoverElegante(pdf, data, col, showPrice = true) {
  // 1) Hero photo full-bleed
  // iter v28 r16 — alta risoluzione (2400px = ~290 DPI a 210mm) + cover-fit
  // per non distorcere foto orizzontali o verticali in un A4 portrait.
  const hero = await loadImageAsJpegDataUrl(data.photos.hero, 2400);
  if (hero) {
    await addPhotoCover(pdf, hero, 0, 0, A4_W, A4_H);
  } else {
    setFill(pdf, "#DDD8CB");
    pdf.rect(0, 0, A4_W, A4_H, "F");
  }

  // 2) Bottom gradient overlay (accent color, 55% height) — simulato con
  // rettangoli a opacità decrescente per dare effetto sfumato.
  const accent = col.isDark ? col.bg : col.accent;
  const [ar, ag, ab] = hexToRgb(accent);
  const overlayStart = A4_H * 0.42;
  const overlayH = A4_H - overlayStart;
  const STEPS = 24;
  for (let i = 0; i < STEPS; i++) {
    const t = i / (STEPS - 1);
    const alpha = Math.min(0.95, t * t * 1.05);
    pdf.setFillColor(ar, ag, ab);
    pdf.setGState(new pdf.GState({ opacity: alpha }));
    pdf.rect(0, overlayStart + (overlayH * i) / STEPS, A4_W, overlayH / STEPS + 0.3, "F");
  }
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  // 3) Logo agenzia (in alto a sinistra, area trasparente sopra foto)
  // iter v28r8: logo più grande (24mm) per maggiore impatto su cover.
  // Logo mono → tinto white (bg foto scura). Colorato → fedele.
  setText(pdf, "#FAFAF7");
  const logoY = 14;
  let logoX = 18;
  if (data.agencyLogoUrl) {
    const logo = await loadAgencyLogo(data, 600, true);
    if (logo) {
      const lw = logo.w, lh = logo.h;
      const scale = Math.min(24 / lw, 24 / lh);
      const dw = lw * scale, dh = lh * scale;
      pdf.addImage(logo.dataUrl, "PNG", logoX, logoY, dw, dh);
      logoX += dw + 5;
    }
  }
  // Agency name italic serif accanto al logo (verticalmente allineato)
  pdf.setFont("times", "italic");
  pdf.setFontSize(16);
  // Shadow nera leggera
  setText(pdf, "#000000");
  pdf.setGState(new pdf.GState({ opacity: 0.45 }));
  pdf.text(safe(data.agencyName, "Agenzia"), logoX + 0.3, logoY + 16.3);
  pdf.setGState(new pdf.GState({ opacity: 1 }));
  setText(pdf, "#FAFAF7");
  pdf.text(safe(data.agencyName, "Agenzia"), logoX, logoY + 16);

  // 4) Title + città — posizione centro-basso, ben sopra il prezzo
  pdf.setFont("times", "italic");
  pdf.setFontSize(30);
  setText(pdf, "#FAFAF7");
  const title = safe(data.title, "Immobile") + ".";
  const titleLines = pdf.splitTextToSize(title, A4_W - 36);
  const titleStartY = A4_H - 78;
  titleLines.slice(0, 3).forEach((line, i) => {
    pdf.text(line, 18, titleStartY + i * 11);
  });

  // città subtitle (solo se non duplicata nel titolo)
  const cityShow = cityIfNotInTitle(data);
  if (cityShow) {
    pdf.setFont("times", "italic");
    pdf.setFontSize(14);
    setText(pdf, "#FAFAF7");
    pdf.setGState(new pdf.GState({ opacity: 0.92 }));
    pdf.text(cityShow, 18, titleStartY + titleLines.length * 11 + 7);
    pdf.setGState(new pdf.GState({ opacity: 1 }));
  }

  // 5) Bottom divider line + prezzo (sx) + mq/locali (dx)
  const bottomY = A4_H - 22;
  setStroke(pdf, "#FAFAF7");
  pdf.setGState(new pdf.GState({ opacity: 0.55 }));
  pdf.setLineWidth(0.25);
  pdf.line(18, bottomY - 6, A4_W - 18, bottomY - 6);
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  if (showPrice) {
    pdf.setFont("times", "italic");
    pdf.setFontSize(28);
    setText(pdf, "#FAFAF7");
    pdf.text(safe(data.price, "—"), 18, bottomY);
  }

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  setText(pdf, "#FAFAF7");
  pdf.setGState(new pdf.GState({ opacity: 0.95 }));
  const meta = `${data.surface || "—"}MQ · ${data.rooms || "—"} LOC.`;
  const metaW = pdf.getTextWidth(meta);
  pdf.text(meta, A4_W - 18 - metaW, bottomY - 1);
  pdf.setGState(new pdf.GState({ opacity: 1 }));
}

// ─── Page 2: DESCRIZIONE (Elegante con drop cap VERO) ──────────────
async function buildDescriptionElegante(pdf, data, col) {
  setFill(pdf, "#FAFAF7");
  pdf.rect(0, 0, A4_W, A4_H, "F");

  setText(pdf, "#1C1C1A");
  pdf.setFont("times", "italic");
  pdf.setFontSize(20);
  pdf.text("La proprietà", 18, 24);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  setText(pdf, "#1C1C1A");
  pdf.setGState(new pdf.GState({ opacity: 0.55 }));
  const numW = pdf.getTextWidth("— 02 —");
  pdf.text("— 02 —", A4_W - 18 - numW, 24);
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  setStroke(pdf, "#1C1C1A");
  pdf.setGState(new pdf.GState({ opacity: 0.14 }));
  pdf.setLineWidth(0.2);
  pdf.line(18, 28, A4_W - 18, 28);
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  setText(pdf, col.accent || "#7C8B6E");
  pdf.text("D E S C R I Z I O N E", 18, 38);

  // Body description con DROP CAP VERO
  const desc = data.description || data.subtitle || `${data.title} di ${data.surface} mq, ${data.rooms} locali.`;
  const trimmed = desc.length > 700 ? desc.slice(0, 697) + "…" : desc;
  const firstChar = trimmed.charAt(0);
  const restText = trimmed.slice(1).replace(/^\s+/, " ");

  // iter v28r8: drop-cap MIGLIORATO — più piccolo (40pt invece di 48pt),
  // baseline allineata alla seconda riga di testo, kerning naturale col primo
  // carattere del resto del testo (no spacing extra).
  const dropX = 18;
  const dropBaseline = 56; // baseline del drop-cap (allineata alla 2a riga)
  pdf.setFont("times", "italic");
  pdf.setFontSize(40);
  setText(pdf, col.accent || "#7C8B6E");
  pdf.text(firstChar, dropX, dropBaseline);
  const dropW = pdf.getTextWidth(firstChar) + 1;

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  setText(pdf, "#1C1C1A");
  const colW = (A4_W - 36) * 0.6;
  const bodyX = 18;
  const bodyY = 47;
  const lineH = 5.2;
  const tightW = colW - dropW;
  const wideW = colW;
  const wrappedTight = pdf.splitTextToSize(restText, tightW);
  const firstThree = wrappedTight.slice(0, 3);
  const usedChars = firstThree.join(" ").length;
  const remaining = restText.slice(usedChars).replace(/^\s+/, "");
  const wrappedRest = pdf.splitTextToSize(remaining, wideW);
  firstThree.forEach((line, i) => {
    pdf.text(line, bodyX + dropW, bodyY + i * lineH);
  });
  const restStartY = bodyY + 3 * lineH + 2;
  let curY = restStartY;
  const maxBodyY = A4_H - 80;
  wrappedRest.forEach((line) => {
    if (curY > maxBodyY) return;
    pdf.text(line, bodyX, curY);
    curY += lineH;
  });

  const photoX = 18 + colW + 6;
  const photoW = A4_W - 18 - photoX;
  // iter v28 r16 — alta risoluzione + cover-fit (era 1000px stretchato)
  const photo2 = data.photos.p2 ? await loadImageAsJpegDataUrl(data.photos.p2, 1800) : null;
  const photo3 = data.photos.p3 ? await loadImageAsJpegDataUrl(data.photos.p3, 1800) : null;
  if (photo2) {
    await addPhotoCover(pdf, photo2, photoX, 44, photoW, 90);
  } else {
    setFill(pdf, "#F1F4ED");
    pdf.roundedRect(photoX, 44, photoW, 90, 1, 1, "F");
  }
  if (photo3) {
    await addPhotoCover(pdf, photo3, photoX, 140, photoW, 60);
  } else if (photo2) {
    await addPhotoCover(pdf, photo2, photoX, 140, photoW, 60);
  } else {
    setFill(pdf, "#F1F4ED");
    pdf.roundedRect(photoX, 140, photoW, 60, 1, 1, "F");
  }

  // iter v28r8: grid attributi a numero VARIABILE (solo quelli con valore).
  // Si ribilancia 1/2/3 colonne in base al count, mai placeholder "—".
  const attrs = getAttrs(data);
  if (attrs.length > 0) {
    const attrY = A4_H - 65;
    const attrCols = Math.min(attrs.length, 3);
    const attrGutter = 3;
    const attrW = (A4_W - 36 - attrGutter * (attrCols - 1)) / attrCols;
    const attrH = 14;
    attrs.forEach(([k, v], i) => {
      const r = Math.floor(i / attrCols);
      const c = i % attrCols;
      const x = 18 + c * (attrW + attrGutter);
      const y = attrY + r * (attrH + 3);
      setFill(pdf, "#1C1C1A");
      pdf.setGState(new pdf.GState({ opacity: 0.05 }));
      pdf.roundedRect(x, y, attrW, attrH, 0.8, 0.8, "F");
      pdf.setGState(new pdf.GState({ opacity: 1 }));
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7);
      setText(pdf, col.accent || "#7C8B6E");
      pdf.text(k, x + 2, y + 5);
      pdf.setFont("times", "italic");
      pdf.setFontSize(11);
      setText(pdf, "#1C1C1A");
      pdf.text(safe(v), x + 2, y + 11.5);
    });
  }

  drawFooter(pdf, data, "02");
}

// ─── Page 3: GALLERIA (Elegante) ───────────────────────────────────
async function buildGalleryElegante(pdf, data, col) {
  setFill(pdf, "#FAFAF7");
  pdf.rect(0, 0, A4_W, A4_H, "F");

  setText(pdf, "#1C1C1A");
  pdf.setFont("times", "italic");
  pdf.setFontSize(20);
  pdf.text("Galleria", 18, 24);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.setGState(new pdf.GState({ opacity: 0.55 }));
  const numW = pdf.getTextWidth("— 03 —");
  pdf.text("— 03 —", A4_W - 18 - numW, 24);
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  setStroke(pdf, "#1C1C1A");
  pdf.setGState(new pdf.GState({ opacity: 0.14 }));
  pdf.setLineWidth(0.2);
  pdf.line(18, 28, A4_W - 18, 28);
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  const photoUrls = [data.photos.hero, data.photos.p2, data.photos.p3, data.photos.p4, data.photos.p5].filter(Boolean);
  // iter v28 r16 — bump risoluzione 1400→2000 (~270 DPI a 12cm)
  const photos = await Promise.all(photoUrls.map((u) => loadImageAsJpegDataUrl(u, 2000)));

  const gridTop = 36;
  const gridBottom = A4_H - 28;
  const gridH = gridBottom - gridTop;
  const gutter = 3;

  if (photos.length >= 4) {
    const topH = photos.length >= 5 ? gridH - 50 - gutter : gridH;
    const heroW = (A4_W - 36) * 0.6;
    const thumbW = (A4_W - 36) * 0.4 - gutter;
    const thumbH = (topH - gutter) / 2;
    if (photos[0]) await addPhotoCover(pdf, photos[0], 18, gridTop, heroW, topH);
    if (photos[1]) await addPhotoCover(pdf, photos[1], 18 + heroW + gutter, gridTop, thumbW, thumbH);
    if (photos[2]) await addPhotoCover(pdf, photos[2], 18 + heroW + gutter, gridTop + thumbH + gutter, thumbW, thumbH);
    if (photos.length >= 5) {
      const bottomY = gridTop + topH + gutter;
      const bottomW = (A4_W - 36 - gutter) / 2;
      const bottomH = 50;
      if (photos[3]) await addPhotoCover(pdf, photos[3], 18, bottomY, bottomW, bottomH);
      if (photos[4]) await addPhotoCover(pdf, photos[4], 18 + bottomW + gutter, bottomY, bottomW, bottomH);
    } else if (photos[3]) {
      const bottomY = gridTop + topH + gutter;
      const bottomW = (A4_W - 36 - gutter) / 2;
      const bottomH = 50;
      await addPhotoCover(pdf, photos[3], 18, bottomY, bottomW, bottomH);
    }
  } else if (photos.length === 3) {
    const heroW = (A4_W - 36) * 0.6;
    const thumbW = (A4_W - 36) * 0.4 - gutter;
    const thumbH = (gridH - gutter) / 2;
    if (photos[0]) await addPhotoCover(pdf, photos[0], 18, gridTop, heroW, gridH);
    if (photos[1]) await addPhotoCover(pdf, photos[1], 18 + heroW + gutter, gridTop, thumbW, thumbH);
    if (photos[2]) await addPhotoCover(pdf, photos[2], 18 + heroW + gutter, gridTop + thumbH + gutter, thumbW, thumbH);
  } else if (photos.length === 2) {
    const w = (A4_W - 36 - gutter) / 2;
    if (photos[0]) await addPhotoCover(pdf, photos[0], 18, gridTop, w, gridH);
    if (photos[1]) await addPhotoCover(pdf, photos[1], 18 + w + gutter, gridTop, w, gridH);
  } else if (photos.length === 1) {
    if (photos[0]) await addPhotoCover(pdf, photos[0], 18, gridTop, A4_W - 36, gridH);
  }

  const extra = Math.max(0, (data.photoTotalCount || 0) - photos.length);
  if (extra > 0) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7);
    setText(pdf, "#1C1C1A");
    pdf.setGState(new pdf.GState({ opacity: 0.55 }));
    const txt = `+ ${extra} FOTO SULLA PAGINA ANNUNCIO · ${(data.agencyWeb || "agenzia.it").toUpperCase()}`;
    const tw = pdf.getTextWidth(txt);
    pdf.text(txt, A4_W - 18 - tw, gridBottom + 3);
    pdf.setGState(new pdf.GState({ opacity: 1 }));
  }

  drawFooter(pdf, data, "03");
}

// ─── Page 4: CONTATTI (Elegante, bg scuro, tutto destra-allineato) ──
async function buildContactsElegante(pdf, data, col, showProntoLabel = true) {
  setFill(pdf, "#1C1C1A");
  pdf.rect(0, 0, A4_W, A4_H, "F");

  // iter v28: logo PNG trasparente posato direttamente (no card bianca).
  // iter v28r8: logo più grande (32mm) per maggiore visibilità sulla pagina contatti.
  let logoBottomY = 24;
  if (data.agencyLogoUrl) {
    const logo = await loadAgencyLogo(data, 600, true);
    if (logo) {
      const lw = logo.w, lh = logo.h;
      const scale = Math.min(32 / lw, 32 / lh);
      const dw = lw * scale, dh = lh * scale;
      pdf.addImage(logo.dataUrl, "PNG", 18, 18, dw, dh);
      logoBottomY = 18 + dh + 4;
    }
  }

  pdf.setFont("times", "italic");
  pdf.setFontSize(22);
  setText(pdf, "#FAFAF7");
  const agency = safe(data.agencyName, "La tua agenzia");
  const agencyLines = pdf.splitTextToSize(agency, A4_W - 50);
  const ah = agencyLines.length;
  agencyLines.slice(0, 2).forEach((line, i) => {
    const w = pdf.getTextWidth(line);
    pdf.text(line, A4_W - 18 - w, 26 + i * 9);
  });

  if (data.agencyWeb) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    setText(pdf, "#FAFAF7");
    pdf.setGState(new pdf.GState({ opacity: 0.55 }));
    const w = pdf.getTextWidth(data.agencyWeb.toUpperCase());
    pdf.text(data.agencyWeb.toUpperCase(), A4_W - 18 - w, 26 + Math.min(ah, 2) * 9 + 3);
    pdf.setGState(new pdf.GState({ opacity: 1 }));
  }

  setStroke(pdf, "#FAFAF7");
  pdf.setGState(new pdf.GState({ opacity: 0.18 }));
  pdf.setLineWidth(0.25);
  pdf.line(18, 50, A4_W - 18, 50);
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  const agentY = A4_H / 2 - 10;
  let agentPhotoX = A4_W - 18;
  if (data.agentPhoto) {
    // iter v28 r16 — bump risoluzione + cover-fit nel quadrato interno
    // del cerchio (così facce/teste restano centrate, non stirate).
    const agentPic = await loadImageAsJpegDataUrl(data.agentPhoto, 900);
    if (agentPic) {
      const r = 14;
      const cx = A4_W - 18 - r;
      const cy = agentY + 6;
      setFill(pdf, "#FAFAF7");
      pdf.circle(cx, cy, r, "F");
      const innerSize = (r - 1.5) * 2;
      await addPhotoCover(pdf, agentPic, cx - r + 1.5, cy - r + 1.5, innerSize, innerSize, { anchor: "top" });
      setStroke(pdf, col.accent || "#C8B886");
      pdf.setLineWidth(0.8);
      pdf.circle(cx, cy, r, "S");
      agentPhotoX = cx - r - 6;
    }
  }

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  setText(pdf, col.accent || "#C8B886");
  const label = "A G E N T E   D I   R I F E R I M E N T O";
  const lw = pdf.getTextWidth(label);
  pdf.text(label, agentPhotoX - lw, agentY);

  pdf.setFont("times", "italic");
  pdf.setFontSize(26);
  setText(pdf, "#FAFAF7");
  const agentName = safe(data.agentName, "Agente immobiliare");
  const agentLines = pdf.splitTextToSize(agentName, agentPhotoX - 18);
  agentLines.slice(0, 2).forEach((line, i) => {
    const w = pdf.getTextWidth(line);
    pdf.text(line, agentPhotoX - w, agentY + 9 + i * 10);
  });

  if (data.agentLicense && data.agentLicense !== "Iscritto al ruolo") {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    setText(pdf, "#FAFAF7");
    pdf.setGState(new pdf.GState({ opacity: 0.55 }));
    const w = pdf.getTextWidth(data.agentLicense.toUpperCase());
    pdf.text(data.agentLicense.toUpperCase(), agentPhotoX - w, agentY + 9 + Math.min(agentLines.length, 2) * 10 + 5);
    pdf.setGState(new pdf.GState({ opacity: 1 }));
  }

  setStroke(pdf, "#FAFAF7");
  pdf.setGState(new pdf.GState({ opacity: 0.18 }));
  pdf.setLineWidth(0.25);
  pdf.line(18, A4_H - 50, A4_W - 18, A4_H - 50);
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  const contactY = A4_H - 38;
  const halfW = (A4_W - 36) / 2 - 4;
  if (data.agentPhone) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7);
    setText(pdf, "#FAFAF7");
    pdf.setGState(new pdf.GState({ opacity: 0.55 }));
    const labW = pdf.getTextWidth("T E L E F O N O");
    pdf.text("T E L E F O N O", 18 + halfW - labW, contactY);
    pdf.setGState(new pdf.GState({ opacity: 1 }));
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    setText(pdf, "#FAFAF7");
    const phoneW = pdf.getTextWidth(data.agentPhone);
    pdf.text(data.agentPhone, 18 + halfW - phoneW, contactY + 6);
  }
  if (data.agentEmail) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7);
    setText(pdf, "#FAFAF7");
    pdf.setGState(new pdf.GState({ opacity: 0.55 }));
    const labW = pdf.getTextWidth("E M A I L");
    pdf.text("E M A I L", A4_W - 18 - labW, contactY);
    pdf.setGState(new pdf.GState({ opacity: 1 }));
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    setText(pdf, "#FAFAF7");
    const emailDisplay = shortenEmail(data.agentEmail, 28);
    const emW = pdf.getTextWidth(emailDisplay);
    pdf.text(emailDisplay, A4_W - 18 - emW, contactY + 6);
  }

  if (showProntoLabel) {
    pdf.setGState(new pdf.GState({ opacity: 0.4 }));
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7);
    setText(pdf, "#FAFAF7");
    const wm = "P R O N T O I M M O . I T";
    const wmW = pdf.getTextWidth(wm);
    pdf.text(wm, A4_W - 18 - wmW, A4_H - 12);
    pdf.setGState(new pdf.GState({ opacity: 1 }));
  }
}

// ─── Footer comune Elegante ────────────────────────────────────────
function drawFooter(pdf, data, num) {
  setStroke(pdf, "#1C1C1A");
  pdf.setGState(new pdf.GState({ opacity: 0.14 }));
  pdf.setLineWidth(0.2);
  pdf.line(18, A4_H - 18, A4_W - 18, A4_H - 18);
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  setText(pdf, "#1C1C1A");
  pdf.setGState(new pdf.GState({ opacity: 0.7 }));
  const city = cleanCity(data.city);
  const label = [data.agencyName, city].filter(Boolean).join(" · ").toUpperCase();
  pdf.text(label, 18, A4_H - 12);
  const pageMark = `${num} · 04`;
  const pmW = pdf.getTextWidth(pageMark);
  pdf.text(pageMark, A4_W - 18 - pmW, A4_H - 12);
  pdf.setGState(new pdf.GState({ opacity: 1 }));
}

// ─── Dispatcher ────────────────────────────────────────────────────
const LAYOUT_BUILDERS = {
  elegante: {
    cover: buildCoverElegante,
    description: buildDescriptionElegante,
    gallery: buildGalleryElegante,
    contacts: buildContactsElegante,
  },
  luxury: {
    cover: buildCoverLuxury,
    description: buildDescriptionLuxury,
    gallery: buildGalleryLuxury,
    contacts: buildContactsLuxury,
  },
  moderno: {
    cover: buildCoverModerno,
    description: buildDescriptionModerno,
    gallery: buildGalleryModerno,
    contacts: buildContactsModerno,
  },
  minimal: {
    cover: buildCoverMinimal,
    description: buildDescriptionMinimal,
    gallery: buildGalleryMinimal,
    contacts: buildContactsMinimal,
  },
};

/**
 * Genera il PDF Brochure 4 pagine A4 in vector nativo.
 *
 * @param {object} data    — output di buildData(property, agency)
 * @param {object} lay     — layout config (LAYOUTS[name])  [unused, retained per API]
 * @param {object} col     — color config (COLORS[key])
 * @param {object} opts    — { showWatermark, showPrice, layoutKey, onProgress }
 * @returns {Promise<Blob>}
 */
export async function generateVectorBrochure({
  data,
  lay,
  col,
  showWatermark = true,
  showPrice = true,
  layoutKey = "elegante",
  onProgress,
}) {
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  const report = (step) => { try { onProgress && onProgress(step); } catch (_) {} };

  const builder = LAYOUT_BUILDERS[layoutKey] || LAYOUT_BUILDERS.elegante;

  report("Carico le foto dell'immobile…");
  await builder.cover(pdf, data, col, showPrice);
  report("Compongo la copertina…");
  pdf.addPage();
  await builder.description(pdf, data, col);
  report("Impagino la descrizione…");
  pdf.addPage();
  await builder.gallery(pdf, data, col);
  report("Sistemo la galleria fotografica…");
  pdf.addPage();
  await builder.contacts(pdf, data, col, showWatermark);
  report("Comprimo il PDF…");

  return pdf.output("blob");
}
